import {Component, linkedSignal, signal} from '@angular/core';
import {EditType} from '../../../../classes/enums/EditType';
import {PreferredTimeOff} from '../../../../classes/models/employee/PreferredTimeOff';
import {TemporaryPreferredTimeOff} from '../../../../classes/models/employee/TemporaryPreferredTimeOff';
import {StoreWorkingTime} from '../../../../classes/models/store/StoreWorkingTime';
import {DayOfWeek} from '../../../../classes/enums/DayOfWeek';
import {EmployeePositionId} from '../../../../classes/models/employee/Employee';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {SHARED_IMPORTS} from "../../../../shared/standalone-imports";
import {TemporaryTimeOffFormComponent} from "../temporary-time-off-form/temporary-time-off-form.component";
import {OngoingTimeOffFormComponent} from "../ongoing-time-off-form/ongoing-time-off-form.component";

/**
 * Data required to open the TimeOffModalComponent.
 */
export interface TimeOffModalData {
  /** An edit action, which is performed by a user. */
  editType: EditType;
  /** Identifiers of an employee and position. */
  employeePositionId: EmployeePositionId;
  /** The date, starting from which weekly preferred times off will take effect. */
  startDate: string;
  /** Ongoing preferred times off for the whole week, which might be edited. */
  weeklyPreferredTimesOff: Map<DayOfWeek, PreferredTimeOff>;
  /** Store earliest and latest working time for a week. */
  storeWorkingTime: StoreWorkingTime;
  /** The day of week, which is being edited by the user. */
  dayOfWeek?: DayOfWeek;
  /** A temporary preferred time off record for selected date, which might be edited. */
  temporaryPreferredTimeOff?: TemporaryPreferredTimeOff;
  /** `True` if there are records of the selected week in backend. */
  weekExists?: boolean;
}

/**
 * This component displays a modal window of ongoing and temporary preferred times off.
 */
@Component({
  selector: 'app-time-off-modal',
  templateUrl: './time-off-modal.component.html',
  styleUrls: ['./time-off-modal.component.less'],
  imports: [SHARED_IMPORTS, TemporaryTimeOffFormComponent, OngoingTimeOffFormComponent]
})
export class TimeOffModalComponent {

  /** An edit action, which is performed by a user. */
  readonly editType = signal<EditType | undefined>(undefined);
  /** Identifiers of an employee and position. */
  readonly employeePositionId = signal<EmployeePositionId | undefined>(undefined);
  /** The date, starting from which weekly preferred times off will take effect. */
  readonly startDate = signal<string | undefined>(undefined);
  /** The day of week, which is being edited by the user. */
  readonly dayOfWeek = signal<DayOfWeek | undefined>(undefined);
  /** Ongoing preferred times off for the whole week, which might be edited. */
  readonly weeklyPreferredTimesOff = signal<Map<DayOfWeek, PreferredTimeOff> | undefined>(undefined);
  /** A temporary preferred time off record for selected date, which might be edited. */
  readonly temporaryPreferredTimeOff = signal<TemporaryPreferredTimeOff | undefined>(undefined);
  /** Store earliest and latest working time for a week. */
  readonly storeWorkingTime = signal<StoreWorkingTime | undefined>(undefined);
  /** `True` if there are records of the selected week in backend. */
  readonly weekExists = signal<boolean | undefined>(undefined);

  /** `True` if a preferred time off is reoccurring, derived from temporaryPreferredTimeOff and writable by user interaction. */
  isReoccurring = linkedSignal(() => !this.temporaryPreferredTimeOff());
  /** Assigning an EditType, so it can be used in HTML. */
  EditType = EditType;

  static open(modalService: NgbModal, data: TimeOffModalData): NgbModalRef {
    const modalRef = modalService.open(TimeOffModalComponent);
    const instance = modalRef.componentInstance as TimeOffModalComponent;

    instance.editType.set(data.editType);
    instance.employeePositionId.set(data.employeePositionId);
    instance.startDate.set(data.startDate);
    instance.weeklyPreferredTimesOff.set(data.weeklyPreferredTimesOff);
    instance.storeWorkingTime.set(data.storeWorkingTime);
    instance.dayOfWeek.set(data.dayOfWeek);
    instance.temporaryPreferredTimeOff.set(data.temporaryPreferredTimeOff);
    instance.weekExists.set(data.weekExists);

    return modalRef;
  }
}